<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/lib/api.js';
import * as XLSX from 'xlsx';

const route = useRoute();

// 학급 데이터 및 로딩 상태
const classData = ref(null);
const students = ref([]);
const loading = ref(true);

// API 호출 함수
const fetchClassResult = async () => {
  try {
    loading.value = true;
    const { exam_id, schul_code, grade_code, school_cls } = route.query;
    
    if (!exam_id || !schul_code || !grade_code || !school_cls) {
      throw new Error('필수 파라미터가 없습니다.');
    }

    console.log('Status 페이지 API 요청:', {
      exam_id,
      schul_code,
      grade_code,
      school_cls
    });

      const response = await api.post('/exam/tea/resultClassDetailLiteracy', {
      exam_id,
      schul_code,
      grade_code,
      school_cls
    });

    if (response.data.success) {
      classData.value = response.data.result;
      
      // 학생 목록 추출
      students.value = response.data.result.list || [];
      
      // 일괄 다운로드를 위해 전역에 저장
      window.currentClassData = response.data.result;
    } else {
      throw new Error(response.data.message || '데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('Status 페이지 데이터 조회 실패:', error);
  } finally {
    loading.value = false;
  }
};


const sortCriteria = ref('none'); 

const sortedStudents = computed(() => {
  const studentsArray = [...students.value]; 

  if (sortCriteria.value === 'none') {
    // 번호 순으로 숫자 정렬
    return studentsArray.sort((a, b) => a.school_num - b.school_num);
  } else if (sortCriteria.value === 'totalOriginalScore') {
    return studentsArray.sort((a, b) => {
      const totalScoreA = a.total_score || 0;
      const totalScoreB = b.total_score || 0;
      return totalScoreB - totalScoreA; 
    });
  }
  return studentsArray;
});

// 학생 개별 결과 조회
const openStudentResult = (student) => {
  const { exam_id } = route.query;
  
  if (!exam_id || !student.user_id) {
    console.error('exam_id 또는 user_id가 없습니다.', { exam_id, user_id: student.user_id });
    return;
  }
  
  const url = `/result/ai-literacy/student?exam_id=${exam_id}&user_id=${student.user_id}`;
  window.open(url, '_blank');
};

// 엑셀 다운로드 함수
const downloadExcel = () => {
  if (!classData.value || !classData.value.list) {
    alert('다운로드할 데이터가 없습니다.');
    return;
  }

  // 엑셀 데이터 생성
  const excelData = sortedStudents.value.map(student => {
    // 탈퇴학생인지 확인
    const isWithdrawnStudent = !student.school_num || student.name === '탈퇴학생';

    return {
      '학교명': student.schul_nm || '',
      '학년': student.grade_code || '',
      '반': student.school_cls || '',
      '번호': isWithdrawnStudent ? '' : student.school_num,
      '이름': student.name,
      '총점수': isWithdrawnStudent ? '' : student.total_score,
      '성취수준': isWithdrawnStudent ? '' : student.achievement_level,
      'AI디지털기술_점수': isWithdrawnStudent ? '' : student.field1_score,
      'AI디지털기술_수준': isWithdrawnStudent ? '' : student.field1_level,
      'AI디지털활용_점수': isWithdrawnStudent ? '' : student.field2_score,
      'AI디지털활용_수준': isWithdrawnStudent ? '' : student.field2_level,
      'AI디지털윤리_점수': isWithdrawnStudent ? '' : student.field3_score,
      'AI디지털윤리_수준': isWithdrawnStudent ? '' : student.field3_level,
      '인간중심AI_점수': isWithdrawnStudent ? '' : student.field4_score,
      '인간중심AI_수준': isWithdrawnStudent ? '' : student.field4_level
    };
  });

  // 워크시트 생성
  const ws = XLSX.utils.json_to_sheet(excelData);
  
  // 워크북 생성
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'AI리터러시결과');

  // 파일명 생성
  const schoolName = sortedStudents.value[0]?.schul_nm || '학교';
  const gradeClass = `${sortedStudents.value[0]?.grade_code}학년${sortedStudents.value[0]?.school_cls}반`;
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  const filename = `${schoolName}_${gradeClass}_AI리터러시결과_${dateStr}.xlsx`;

  // 파일 다운로드
  XLSX.writeFile(wb, filename);
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(() => {
  fetchClassResult();
});


const sortOptions = ref([
  {
    label: '번호 순', 
    value: 'none'     
  },
  {
    label: '총점수 순',
    value: 'totalOriginalScore'
  }
]);
</script>

<template>
  <div class="table-container">
    <div class="table-top">
      <div class="title">
        <h5>우리 반 AI 리터러시 결과 현황</h5>
        <div class="sort-options">       
        <n-select
          id="sortOrder"
          v-model:value="sortCriteria"
          :options="sortOptions"
          style="width:150px;" 
        />
      </div>
      </div>
      <p class="download-button" @click="downloadExcel">AI 리터러시 결과 엑셀 다운로드</p>     
    </div>    
   
    <table>
      <thead>       
        <tr>
          <th rowspan="3">반</th>
          <th rowspan="3">번호</th> 
          <th rowspan="3">이름</th>
          <th rowspan="3">결과지</th> 
          <th colspan="2">전체</th> 
          <th colspan="2">AI·디지털 기술</th> 
          <th colspan="2">AI·디지털 활용</th>
          <th colspan="2">AI·디지털 윤리</th>
          <th colspan="2">인간 중심 AI</th>
        </tr>        
        <tr>
          <th rowspan="2">점수</th> 
          <th rowspan="2">성취수준</th> 
          <th rowspan="2">점수</th> 
          <th rowspan="2">수준</th>
          <th rowspan="2">점수</th> 
          <th rowspan="2">수준</th> 
          <th rowspan="2">점수</th> 
          <th rowspan="2">수준</th>
          <th rowspan="2">점수</th> 
          <th rowspan="2">수준</th>  

        </tr>
      </thead>
      <tbody>
        <tr v-if="loading">
          <td colspan="14" class="text-center py-4">
            데이터를 불러오는 중...
          </td>
        </tr>
        <tr v-else-if="sortedStudents.length === 0">
          <td colspan="14" class="text-center py-4">
            학생 데이터가 없습니다.
          </td>
        </tr>
        <tr v-else v-for="(student, index) in sortedStudents" :key="student.school_num">
          <td>{{ student.grade_code }}-{{ student.school_cls }}</td>
          <td>{{ student.school_num }}</td>
          <td>{{ student.name }}</td>
          <td>
            <button 
              v-if="student.school_num && student.name !== '탈퇴학생'" 
              @click="openStudentResult(student)"
            >
              조회
            </button>
          </td> 
          <td>{{ student.name === '탈퇴학생' ? '' : student.total_score }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.achievement_level }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.field1_score }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.field1_level }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.field2_score }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.field2_level }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.field3_score }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.field3_level }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.field4_score }}</td>
          <td>{{ student.name === '탈퇴학생' ? '' : student.field4_level }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.table-container {
    background-color: #fff;
    padding: 20px 20px 30px 20px;
    border-radius: 15px;
}

.table-top {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;  
  padding-bottom: 20px; 
  .title {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .download-button {
    background-color: var(--smoe-primary-500);
    color: #fff;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    height: 40px;
    line-height: 40px;
  } 
}
.info {
  display: flex;
  justify-content: space-between;
  text-align: center;  
  padding: 20px 0;
  .text-wrap {
    border: 1px solid #ccc;
    border-radius: 8px;
    width: calc((100% - 16px )/2);
    padding: 20px 10px;
    font-weight: 500;
    &:nth-child(1) {
      border-color: var(--result-light-blue);
      color: var(--result-light-blue);
    }
    &:nth-child(2) {
      border-color: var(--result-light-red);
      color: var(--result-light-red);
    }
  }
}

table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
  font-size: 16px;
}
th, td {
  border: 1px solid #ccc; padding: 8px; vertical-align: middle;
}
th {
  background-color: #f2f2f2; font-weight: 500;
}

button {
  padding: 4px 10px;
  border: 1px solid var(--smoe-primary-500);
  color: var(--smoe-primary-500);
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px; 
}

// 반복문으로 컬럼 너비 처리 (총 14개) 
@for $i from 1 through 14 { 
  // 반, 번호, 이름, 결과지(조회버튼)
  @if $i == 1 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 80px; } // 반
  } @else if $i == 2 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 50px; } // 번호
  } @else if $i == 3 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 80px; } // 이름
  } @else if $i == 4 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 60px; } // 결과지
  } 
  // 전체 점수, 성취수준 (5번~6번 컬럼)
  @else if $i >= 5 and $i <= 6 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 80px; }
  }
  // AI 영역별 점수, 수준 컬럼 (7번~14번 컬럼)
  @else if $i >= 7 and $i <= 14 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 70px; } 
  }
}

// 셀렉트 박스
.sort-options {  
  display: flex; 
  align-items: center; 

  label {
    font-weight: bold;
    color: #333;
    white-space: nowrap; 
  }

 :deep(.n-base-selection) {
    height: 40px !important; 
    min-height: 40px !important; 
    
    .n-base-selection-label {
      height: 40px !important; 
      line-height: 40px !important; 
      padding: 0 5px 0 15px !important; 
    }      
  }
  :deep(.n-base-selection-input) {padding: 0; }  
}

// AI 리터러시 컬럼 색상
$ai-primary-color: var(--smoe-primary-500);
$ai-secondary-color: #6366f1;
$ai-tertiary-color: #059669;
$ai-quaternary-color: #dc2626;

// 첫 번째 행 메인 헤더 색상
tr:nth-child(1) {
  th:nth-child(5) { color: $ai-primary-color; } // 전체
  th:nth-child(6) { color: $ai-tertiary-color; } // AI디지털기술
  th:nth-child(7) { color: $ai-quaternary-color; } // AI디지털활용
  th:nth-child(8) { color: $ai-tertiary-color } // AI디지털윤리
  th:nth-child(9) { color: $ai-quaternary-color; } // 인간중심 AI
}

// 두 번째 행 하위 헤더 색상
tr:nth-child(2) {
  th:nth-child(1), th:nth-child(2) { color: $ai-primary-color; } // 전체 영역
  th:nth-child(3), th:nth-child(4) { color: $ai-tertiary-color } // AI디지털기술
  th:nth-child(5), th:nth-child(6) { color: $ai-quaternary-color;; } // AI디지털활용
  th:nth-child(7), th:nth-child(8) { color: $ai-tertiary-color } // AI디지털윤리
  th:nth-child(9), th:nth-child(10) { color: $ai-quaternary-color; } // 인간중심 AI
}
</style>